import { DependencyList, useCallback, useState } from 'react';
import { useDebounce, useString } from '../../../../helpers';
import { usePaginator } from '../paginator/d-paginator';

// Shared tuning for every paginated list block.
const FILTER_DEBOUNCE_MS = 400;
const EMBEDDED_PAGE_SIZE = 10;

/**
 * Identifies the parent record a list block is embedded under. When a `scope`
 * is given the block stops showing the *main* entity (sales/productions/…) as a
 * standalone page and instead shows only the rows that reference that parent —
 * e.g. all sales of a product, or (later) all productions on a machine. The
 * discriminant keeps it open for new parents without growing a pile of
 * `productId` / `machineId` booleans.
 */
export type ProductScope = { type: 'product'; id: number };
export type MachineScope = { type: 'machine'; id: number };
export type AccountScope = { type: 'account'; id: number };
export type TransferTypeScope = { type: 'transferType'; id: number };
export type ListScope = ProductScope | MachineScope | AccountScope | TransferTypeScope;

interface IUsePaginatedListBlock {
    // Namespaces all localStorage keys (filter/dates/pagination). Pass a unique
    // value when the same block is embedded more than once (e.g. product page).
    storageKey: string;
    // Embedded (scoped-to-a-parent) variant — uses a smaller page size.
    embedded?: boolean;
    // Block-specific filter values (account/type/branch…) that should reset the
    // pagination back to page 0 when they change. The general filter + date
    // range are already wired in.
    resetDeps?: DependencyList;
}

/**
 * Encapsulates the state every paginated list block shares: the general
 * (debounced) text filter, the date range, the offset paginator, and the
 * edit/delete dialog state. Blocks keep only their own extra filters, query,
 * columns, and dialogs.
 */
export function usePaginatedListBlock({
    storageKey,
    embedded = false,
    resetDeps = [],
}: IUsePaginatedListBlock) {
    const [filter, setFilter] = useString({ KEY: storageKey + '_Filter' });
    const debouncedFilter = useDebounce(filter, FILTER_DEBOUNCE_MS);

    const [startDate, setStartDate] = useString({ KEY: storageKey + '_StartDate' });
    const [endDate, setEndDate] = useString({ KEY: storageKey + '_EndDate' });

    const { take, skip, setSkip } = usePaginator(
        {
            KEY: storageKey + '_Paginator',
            take: embedded ? EMBEDDED_PAGE_SIZE : undefined,
        },
        [startDate, endDate, debouncedFilter, ...resetDeps],
    );

    // Shared edit/delete dialog state (the selected row id + open flags).
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const openEdit = useCallback((id: number) => {
        setSelectedId(id);
        setUpsertOpen(true);
    }, []);

    const openDelete = useCallback((id: number) => {
        setSelectedId(id);
        setDeleteOpen(true);
    }, []);

    return {
        filter,
        setFilter,
        debouncedFilter,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        skip,
        take,
        setSkip,
        selectedId,
        setSelectedId,
        upsertOpen,
        setUpsertOpen,
        deleteOpen,
        setDeleteOpen,
        openEdit,
        openDelete,
    };
}

/**
 * Derives the current `docs`/`count` from an Apollo paginated result, falling
 * back to `previousData` so the table doesn't flash empty between pages.
 */
export function selectPaginated<T>(
    current: { docs: T[]; count: number } | undefined,
    previous: { docs: T[]; count: number } | undefined,
): { docs: T[]; count: number | undefined } {
    return {
        docs: current?.docs ?? previous?.docs ?? [],
        count:
            current && current.count >= 0 ? current.count : previous?.count,
    };
}
